import { createMatchEndBannerController } from './matchEndBannerController.js';
import { buildMatchScoreboard } from '../../application/matchScoreboard.js';
import { resolveMatchEndPostGameAction } from '../../domain/match/matchEndPostGame.js';

/**
 * wireMatchEndUi — Banner + scoreboard + rematch/lobby polish (SPY-42 / SPY-114 / SPY-215).
 */
export function wireMatchEndUi({
  documentRef = globalThis.document,
  getPlayerTeam,
  getScoreboardRows,
  matchRuntime,
  inputController,
  matchTelemetry,
  onContinue,
  onRematch,
  onReturnLobby,
  globalObj = typeof window !== 'undefined' ? window : undefined
} = {}) {
  const rootEl = documentRef?.getElementById?.('match-end-overlay') || null;
  const titleEl = documentRef?.getElementById?.('match-end-title') || null;
  const summaryTbodyEl =
    documentRef?.getElementById?.('match-end-scoreboard-tbody') || null;
  const telemetryEl =
    documentRef?.getElementById?.('match-end-telemetry') || null;
  const continueBtnEl =
    documentRef?.getElementById?.('match-end-continue-btn') || null;
  const rematchBtnEl =
    documentRef?.getElementById?.('match-end-rematch-btn') || null;

  const resolveRows =
    typeof getScoreboardRows === 'function'
      ? getScoreboardRows
      : () => buildMatchScoreboard(matchRuntime);

  let lastRecord = null;
  let handlers = {
    onContinue,
    onRematch,
    onReturnLobby
  };

  const banner = createMatchEndBannerController({
    rootEl,
    titleEl,
    summaryTbodyEl,
    telemetryEl,
    continueBtnEl,
    getPlayerTeam,
    getRows: resolveRows,
    getTelemetry: () => lastRecord || matchTelemetry?.get?.() || null
  });

  function onEnded(outcome) {
    if (matchTelemetry && typeof matchTelemetry.end === 'function') {
      lastRecord = matchTelemetry.end({
        winTeam: outcome?.winnerTeam ?? null,
        forfeit: !!outcome?.forfeit
      });
    } else if (matchTelemetry && typeof matchTelemetry.get === 'function') {
      lastRecord = matchTelemetry.get();
    }
    banner.show(outcome);
    if (globalObj?.__debugHonor && typeof globalObj.__debugHonor.showChip === 'function') {
      globalObj.__debugHonor.showChip();
    }
    if (inputController && typeof inputController.setInputFrozen === 'function') {
      inputController.setInputFrozen(true);
    }
  }

  function reset() {
    banner.hide();
    if (globalObj?.__debugHonor && typeof globalObj.__debugHonor.hideChip === 'function') {
      globalObj.__debugHonor.hideChip();
    }
    if (inputController && typeof inputController.setInputFrozen === 'function') {
      inputController.setInputFrozen(false);
    }
  }

  function restartTelemetry() {
    if (matchTelemetry && typeof matchTelemetry.start === 'function') {
      matchTelemetry.start();
    }
  }

  function runPostGame(actionId) {
    const action = resolveMatchEndPostGameAction(actionId);
    if (!action) return false;
    reset();
    if (action.restartsTelemetry) restartTelemetry();
    if (actionId === 'rematch') {
      if (typeof handlers.onRematch === 'function') handlers.onRematch();
      return true;
    }
    if (typeof handlers.onReturnLobby === 'function') handlers.onReturnLobby();
    else if (typeof handlers.onContinue === 'function') handlers.onContinue();
    return true;
  }

  function handleContinue() {
    return runPostGame('lobby');
  }

  function handleRematch() {
    return runPostGame('rematch');
  }

  if (continueBtnEl && typeof continueBtnEl.addEventListener === 'function') {
    continueBtnEl.addEventListener('click', handleContinue);
  }
  if (rematchBtnEl && typeof rematchBtnEl.addEventListener === 'function') {
    rematchBtnEl.addEventListener('click', handleRematch);
  }

  function bindPostGame(next = {}) {
    handlers = { ...handlers, ...next };
    return handlers;
  }

  if (globalObj) {
    globalObj.__debugMatchEnd = {
      show: (outcome) => onEnded(outcome || {
        ended: true,
        winnerTeam: 'blue',
        loserTeam: 'red'
      }),
      hide: () => reset(),
      continue: () => handleContinue(),
      rematch: () => handleRematch(),
      returnLobby: () => handleContinue(),
      isVisible: () => banner.isVisible(),
      getSummary: () => banner.getSummaryRows(),
      getTelemetry: () => banner.getTelemetrySnapshot(),
      bindPostGame
    };
  }

  return {
    onEnded,
    reset,
    continue: handleContinue,
    rematch: handleRematch,
    returnLobby: handleContinue,
    bindPostGame,
    banner,
    debugApi: globalObj?.__debugMatchEnd || null
  };
}
